/**
 * SEMANTIC FILTER SYSTEM - FRONTEND API CLIENT
 * 
 * Provides complete integration between frontend and 6-filter + 6-failsafe backend system
 */

import http from './httpClient';

const API_BASE = '/stock-in/v2';

/**
 * ============================================================
 * SEMANTIC FILTER API CALLS (6 Filters)
 * ============================================================
 */

/**
 * Process single line item through all 6 filters
 * Returns: matched item, unit corrections, cost validation, etc.
 */
export const processLineItem = async (lineItem, context = {}) => {
  try {
    const response = await http.post(`${API_BASE}/semantic-process-line-item`, {
      lineItem,
      context
    });
    return response;
  } catch (error) {
    console.error('❌ Filter processing error:', error);
    throw new Error(error.response?.data?.error || 'Filter processing failed');
  }
};

/**
 * Process entire batch through all 6 filters + Filter 6 validation
 * Returns: processed batch with all corrections, warnings, and blocking errors
 */
export const processBatch = async (batchData, lineItems, context = {}) => {
  try {
    const response = await http.post(`${API_BASE}/semantic-process-batch`, {
      batchData,
      lineItems,
      context
    });
    return response;
  } catch (error) {
    console.error('❌ Batch processing error:', error);
    throw new Error(error.response?.data?.error || 'Batch processing failed');
  }
};

/**
 * Train Filter 1 (SMMF) - Save custom OCR → Inventory mapping
 */
export const trainMapping = async (ocrName, mappedItemId, vendorId = null, userId = null) => {
  try {
    const response = await http.post(`${API_BASE}/train-mapping`, {
      ocrName,
      mappedItemId,
      vendorId,
      userId
    });
    return response;
  } catch (error) {
    console.error('❌ Mapping training error:', error);
    throw new Error(error.response?.data?.error || 'Failed to save mapping');
  }
};

/**
 * Train Filter 2 (SUF) - Save custom unit conversion (Ripple Effect)
 */
export const trainUnitConversion = async (rawUnit, standardUnit, conversionFactor, itemName, userId = null) => {
  try {
    const response = await http.post(`${API_BASE}/train-unit-conversion`, {
      rawUnit,
      standardUnit,
      conversionFactor,
      itemName,
      userId
    });
    return response;
  } catch (error) {
    console.error('❌ Unit training error:', error);
    throw new Error(error.response?.data?.error || 'Failed to save unit conversion');
  }
};

/**
 * ============================================================
 * FAILSAFE SYSTEM API CALLS (6 Failsafes)
 * ============================================================
 */

/**
 * FAILSAFE 1: Confirm auto-correction (yellow → confirmed)
 */
export const confirmCorrection = async (lineItemId, userId = null) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/confirm-correction`, {
      lineItemId,
      userId
    });
    return response;
  } catch (error) {
    console.error('❌ Confirmation error:', error);
    throw new Error(error.response?.data?.error || 'Failed to confirm correction');
  }
};

/**
 * FAILSAFE 1: Check for pending confirmations (blocks commit)
 */
export const checkPendingConfirmations = async (batchId) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/check-pending-confirmations`, {
      batchId
    });
    return response;
  } catch (error) {
    console.error('❌ Pending check error:', error);
    throw new Error(error.response?.data?.error || 'Failed to check confirmations');
  }
};

/**
 * FAILSAFE 2: Check for duplicate item
 */
export const checkDuplicate = async (lineItem, sessionId = null) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/check-duplicate`, {
      lineItem,
      sessionId: sessionId || `session-${Date.now()}`
    });
    return response;
  } catch (error) {
    console.error('❌ Duplicate check error:', error);
    throw new Error(error.response?.data?.error || 'Failed to check duplicate');
  }
};

/**
 * FAILSAFE 2: Retain duplicate as legitimate
 */
export const retainDuplicate = async (lineItemId, userId = null) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/duplicate-retain`, {
      lineItemId,
      userId
    });
    return response;
  } catch (error) {
    console.error('❌ Retain duplicate error:', error);
    throw new Error(error.response?.data?.error || 'Failed to retain duplicate');
  }
};

/**
 * FAILSAFE 2: Remove duplicate item
 */
export const removeDuplicate = async (lineItemId, userId = null) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/duplicate-remove`, {
      lineItemId,
      userId
    });
    return response;
  } catch (error) {
    console.error('❌ Remove duplicate error:', error);
    throw new Error(error.response?.data?.error || 'Failed to remove duplicate');
  }
};

/**
 * FAILSAFE 3: Generate final audit report with red flags
 */
export const getFinalAuditReport = async (batchId) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/final-audit`, {
      batchId
    });
    return response;
  } catch (error) {
    console.error('❌ Audit report error:', error);
    throw new Error(error.response?.data?.error || 'Failed to generate audit report');
  }
};

/**
 * FAILSAFE 3: Commit batch with red flags (owner approval)
 */
export const commitWithRedFlags = async (batchId, userId = null) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/commit-with-red-flags`, {
      batchId,
      userId
    });
    return response;
  } catch (error) {
    console.error('❌ Commit error:', error);
    throw new Error(error.response?.data?.error || 'Failed to commit batch');
  }
};

/**
 * FAILSAFE 3: Return to editing mode
 */
export const returnToEdit = async (batchId) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/return-to-edit`, {
      batchId
    });
    return response;
  } catch (error) {
    console.error('❌ Return to edit error:', error);
    throw new Error(error.response?.data?.error || 'Failed to return to edit');
  }
};

/**
 * FAILSAFE 4: Create support correction request
 */
export const createSupportRequest = async (requestData) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/support-request`, requestData);
    return response;
  } catch (error) {
    console.error('❌ Support request error:', error);
    throw new Error(error.response?.data?.error || 'Failed to create support request');
  }
};

/**
 * FAILSAFE 5: Check OCR quality with WCCS calculation
 * Returns: { success, blocked, quality_report, ocr_data }
 */
export const checkOCRQuality = async (ocrResult, batchId = null) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/ocr-quality-check`, {
      ocrResult,
      batchId
    });
    return response;
  } catch (error) {
    console.error('❌ OCR quality check error:', error);
    throw new Error(error.response?.data?.error || 'Failed to check OCR quality');
  }
};

/**
 * FAILSAFE 5: Request rescan for low quality OCR
 */
export const requestRescan = async (batchId) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/ocr-rescan-request`, {
      batchId
    });
    return response;
  } catch (error) {
    console.error('❌ Rescan request error:', error);
    throw new Error(error.response?.data?.error || 'Failed to request rescan');
  }
};

/**
 * FAILSAFE 5: Manual override for low quality (emergency only)
 */
export const manualOverrideQuality = async (batchId, userId = null) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/ocr-manual-override`, {
      batchId,
      acknowledgment: 'I_UNDERSTAND_RISKS',
      userId
    });
    return response;
  } catch (error) {
    console.error('❌ Manual override error:', error);
    throw new Error(error.response?.data?.error || 'Failed to override quality gate');
  }
};

/**
 * FAILSAFE 6: Validate financial structure (currency, tax)
 * Returns: { success, financial_mapping, currency_result, tax_check, final_cost }
 */
export const validateFinancialStructure = async (ocrResult, batchId = null, userId = null) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/financial-validation`, {
      ocrResult,
      batchId,
      userId
    });
    return response;
  } catch (error) {
    console.error('❌ Financial validation error:', error);
    throw new Error(error.response?.data?.error || 'Failed to validate financial structure');
  }
};

/**
 * FAILSAFE 6: Save manual currency conversion rate
 */
export const saveConversionRate = async (fromCurrency, toCurrency, rate, userId = null) => {
  try {
    const response = await http.post(`${API_BASE}/failsafe/save-conversion-rate`, {
      fromCurrency,
      toCurrency,
      rate,
      userId
    });
    return response;
  } catch (error) {
    console.error('❌ Save conversion rate error:', error);
    throw new Error(error.response?.data?.error || 'Failed to save conversion rate');
  }
};

/**
 * ============================================================
 * INTEGRATED WORKFLOW HELPERS
 * ============================================================
 */

/**
 * Complete OCR Processing Pipeline
 * Runs OCR Quality Gate (Failsafe 5) → Financial Validation (Failsafe 6) → Semantic Filters (1-6)
 */
export const processOCRComplete = async (ocrData, batchId = null, userId = null) => {
  try {
    
    // Step 1: OCR Quality Gate (Failsafe 5)
    const qualityCheck = await checkOCRQuality(ocrData, batchId);
    
    if (qualityCheck.blocked) {
      return {
        success: false,
        blocked: true,
        step: 'quality_gate',
        quality_report: qualityCheck.quality_report,
        message: 'OCR quality too low. Please rescan with better image quality.'
      };
    }
    
    
    // Step 2: Financial Validation (Failsafe 6)
    const financialValidation = await validateFinancialStructure(ocrData, batchId, userId);
    
    if (!financialValidation.success) {
      if (financialValidation.requires_user_input) {
        return {
          success: false,
          blocked: false,
          step: 'financial_validation',
          requires_user_input: true,
          input_type: financialValidation.input_type,
          financial_mapping: financialValidation.financial_mapping,
          message: financialValidation.message
        };
      }
      
      throw new Error('Financial validation failed');
    }
    
    
    // Step 3: Process through semantic filters
    const lineItems = ocrData.items || [];
    
    const batchData = {
      business_id: ocrData.business_id,
      vendor_id: ocrData.vendor_id,
      invoice_reference: ocrData.invoice_reference,
      received_date: ocrData.received_date
    };
    
    const filterResult = await processBatch(batchData, lineItems, {
      business_id: ocrData.business_id,
      vendor_id: ocrData.vendor_id
    });
    
    
    return {
      success: true,
      blocked: false,
      quality_report: qualityCheck.quality_report,
      financial_result: financialValidation,
      filter_result: filterResult,
      message: 'OCR processed successfully through all gates and filters'
    };
    
  } catch (error) {
    console.error('❌ Complete OCR pipeline error:', error);
    return {
      success: false,
      blocked: false,
      error: error.message,
      message: 'Pipeline processing failed'
    };
  }
};

/**
 * Display user-friendly filter status
 */
export const getFilterStatusDisplay = (filterResult) => {
  if (!filterResult) return { status: 'unknown', color: 'gray', message: 'No filter data' };
  
  const { match_status, confidence, correction_applied, duplicate_flag } = filterResult;
  
  // RED FLAGS
  if (match_status === 'none' || match_status === 'pending') {
    return {
      status: 'error',
      color: 'red',
      message: 'Item not matched - manual selection required',
      blocking: true
    };
  }
  
  if (duplicate_flag === 'Potential_Duplicate') {
    return {
      status: 'warning',
      color: 'red',
      message: 'Potential duplicate detected - confirm or remove',
      blocking: true
    };
  }
  
  // YELLOW WARNINGS
  if (correction_applied && filterResult.audit_flag === 'Pending_User_Confirmation') {
    return {
      status: 'warning',
      color: 'yellow',
      message: 'Auto-correction applied - please confirm',
      blocking: false,
      requires_confirmation: true
    };
  }
  
  if (confidence && confidence < 0.85) {
    return {
      status: 'warning',
      color: 'yellow',
      message: `Low confidence match (${(confidence * 100).toFixed(0)}%) - verify item`,
      blocking: false
    };
  }
  
  // GREEN SUCCESS
  return {
    status: 'success',
    color: 'green',
    message: 'Item validated successfully',
    blocking: false
  };
};

export default {
  // Filters
  processLineItem,
  processBatch,
  trainMapping,
  trainUnitConversion,
  
  // Failsafes
  confirmCorrection,
  checkPendingConfirmations,
  checkDuplicate,
  retainDuplicate,
  removeDuplicate,
  getFinalAuditReport,
  commitWithRedFlags,
  returnToEdit,
  createSupportRequest,
  checkOCRQuality,
  requestRescan,
  manualOverrideQuality,
  validateFinancialStructure,
  saveConversionRate,
  
  // Integrated workflows
  processOCRComplete,
  getFilterStatusDisplay
};
